// Authentication utilities for Cognito integration.
import { getCognitoConfig } from './common/constants';

/**
 * Cookie name for the Cognito ID token. Must match ID_TOKEN_COOKIE_NAME in
 * SmartWaterCDK's edge auth Lambda (SmartWaterWebsiteResources/lambdaRuntime/index.ts)
 * -- that Lambda@Edge reads this exact cookie to gate the CloudFront distribution.
 */
const ID_TOKEN_COOKIE_NAME = 'smartwater_id_token';

export interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

/**
 * Sets the ID token as a cookie readable by the edge auth Lambda (Lambda@Edge
 * can only inspect request cookies/headers, not localStorage). `Secure` is
 * omitted on http:// origins (local dev) since the browser silently drops
 * Secure cookies set over plain HTTP.
 */
function setIdTokenCookie(idToken: string): void {
  const secureAttribute = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${ID_TOKEN_COOKIE_NAME}=${encodeURIComponent(idToken)}; SameSite=Lax; Path=/${secureAttribute}`;
}

/** Clears the ID token cookie by setting an already-expired Max-Age. */
function clearIdTokenCookie(): void {
  document.cookie = `${ID_TOKEN_COOKIE_NAME}=; SameSite=Lax; Path=/; Max-Age=0`;
}

const TOKENS_STORAGE_KEY = 'smartwater_tokens';

/**
 * Client-side OAuth authorization-code exchange and session management for the
 * Cognito + Amazon Federate login flow. Mirrors MrPibbWebsite's `AuthService`
 * (per specs/open/smartwater-pipeline-status-dashboard.md's explicit direction
 * to follow that package's equivalent client auth service).
 *
 * This closes the gap the edge auth Lambda depends on: after a successful
 * Federate login, Cognito redirects the browser back to the bare origin with
 * `?code=...&state=...` in the querystring. Nothing set the
 * `smartwater_id_token` cookie the edge Lambda checks until this service's
 * `exchangeCodeForTokens` runs -- without it, a cold browser (no pre-existing
 * cookie) loops on login indefinitely, since the app shell that would run this
 * exchange never got past the edge Lambda's gate in the first place. The edge
 * Lambda explicitly lets the `?code=...` request through ungated (see
 * `handleRequest`'s early return) specifically so this exchange can run.
 */
export class AuthService {
  private static instance: AuthService;
  private tokens: TokenResponse | null = null;

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /** Redirects to the Cognito Hosted UI's Federate-backed authorize endpoint. */
  login(): void {
    const config = getCognitoConfig();
    const authUrl =
      `https://${config.domain}/oauth2/authorize?` +
      'response_type=code&' +
      `client_id=${config.clientId}&` +
      `redirect_uri=${encodeURIComponent(config.redirectUri)}&` +
      'scope=openid&' +
      'identity_provider=AmazonFederateCDK';

    window.location.href = authUrl;
  }

  /**
   * Exchanges an authorization code (from the `?code=...` querystring Cognito
   * redirects back with) for tokens, persists them, and sets the ID token
   * cookie the edge Lambda validates on subsequent navigations.
   *
   * @throws Error if the token endpoint responds with a non-2xx status.
   */
  async exchangeCodeForTokens(code: string): Promise<TokenResponse> {
    const config = getCognitoConfig();
    const tokenUrl = `https://${config.domain}/oauth2/token`;

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: config.clientId,
        code,
        redirect_uri: config.redirectUri,
      }),
    });

    if (!response.ok) {
      throw new Error(`Token exchange failed: ${response.status}`);
    }

    const tokens: TokenResponse = await response.json();
    this.tokens = tokens;
    localStorage.setItem(TOKENS_STORAGE_KEY, JSON.stringify(tokens));
    setIdTokenCookie(tokens.id_token);
    return tokens;
  }

  /** Returns stored tokens (memory first, then localStorage), or null if none exist or storage is corrupt. */
  getTokens(): TokenResponse | null {
    if (this.tokens) {
      return this.tokens;
    }

    const stored = localStorage.getItem(TOKENS_STORAGE_KEY);
    if (stored) {
      try {
        this.tokens = JSON.parse(stored);
        return this.tokens;
      } catch (error) {
        console.error('Error parsing stored tokens:', error);
        localStorage.removeItem(TOKENS_STORAGE_KEY);
      }
    }

    return null;
  }

  /** True if any tokens are stored, regardless of expiry -- see isTokenExpired for freshness. */
  isAuthenticated(): boolean {
    return this.getTokens() !== null;
  }

  /** True if there is no ID token, it is malformed, or it expires within 60 seconds. */
  isTokenExpired(): boolean {
    const token = this.getIdToken();
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now() + 60000;
    } catch {
      return true;
    }
  }

  /**
   * Refreshes tokens using the stored refresh token. The refresh grant's response
   * omits `refresh_token` (Cognito does not rotate it), so the existing one is
   * preserved rather than dropped.
   *
   * @returns true if the refresh succeeded, false on any failure (no refresh
   *   token available, non-2xx response, or network error) -- callers should
   *   treat false as "re-authenticate," not retry indefinitely.
   */
  async refreshTokens(): Promise<boolean> {
    const tokens = this.getTokens();
    if (!tokens?.refresh_token) return false;

    try {
      const config = getCognitoConfig();
      const tokenUrl = `https://${config.domain}/oauth2/token`;
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: config.clientId,
          refresh_token: tokens.refresh_token,
        }),
      });

      if (!response.ok) return false;

      const newTokens: TokenResponse = await response.json();
      this.tokens = { ...newTokens, refresh_token: tokens.refresh_token };
      localStorage.setItem(TOKENS_STORAGE_KEY, JSON.stringify(this.tokens));
      setIdTokenCookie(this.tokens.id_token);
      return true;
    } catch {
      return false;
    }
  }

  /** Returns the stored access token, or null if none exists. */
  getAccessToken(): string | null {
    return this.getTokens()?.access_token ?? null;
  }

  /** Returns the stored ID token, or null if none exists. */
  getIdToken(): string | null {
    return this.getTokens()?.id_token ?? null;
  }

  /** Clears local session state and redirects to Cognito's federated logout endpoint. */
  logout(): void {
    this.tokens = null;
    localStorage.removeItem(TOKENS_STORAGE_KEY);
    clearIdTokenCookie();

    const config = getCognitoConfig();
    const logoutUrl =
      `https://${config.domain}/logout?` + `client_id=${config.clientId}&` + `logout_uri=${encodeURIComponent(config.redirectUri)}`;

    window.location.href = logoutUrl;
  }
}

export const authService = AuthService.getInstance();
